import type { ComponentPropsWithAs } from '@/@types/common'
import type { Topic as TopicType } from '@/@types/topic'
import { css } from '@emotion/react'
import { CloseIcon, TopicIconBlueIcon } from '@ruguoapp/jike-fe-icons'
import { debounce, isEmpty } from 'lodash-es'
import { type ElementType, useRef } from 'react'
import ClickWrapper from './ClickWrapper'

export interface TopicProps {
	data: TopicType | undefined
	cleanable?: boolean
	onClean?: () => void
	onSearch?: (keyword: string) => void
}

export default function Topic<TElement extends ElementType = 'div'>({
	as: As,
	data,
	cleanable = false,
	onClean,
	onSearch,
	...props
}: ComponentPropsWithAs<TopicProps, TElement>) {
	const isInputCompositingRef = useRef(false)
	const handleChangeRef = useRef(
		debounce(
			(value) => {
				onSearch?.(value)
			},
			300,
			{
				trailing: true,
			}
		)
	)
	if (!data && !cleanable) {
		return null
	}
	const isPlaceholderDisplay = isEmpty(data?.content)
	const Component = As ?? 'div'
	return (
		<Component
			css={css`
				width: fit-content;
				height: 26px;
				padding: 4px 8px 4px 4px;
				border-radius: 15px;
				background: var(--bg-on-body-2, #f0f3f5);
				display: inline-flex;
				gap: 2px;
				align-items: center;
				user-select: none;
			`}
			{...props}
		>
			<TopicIconBlueIcon
				css={css`
					width: 18px;
					height: 18px;
				`}
			/>
			<div
				css={css`
					color: ${isPlaceholderDisplay
						? 'var(--tint-tertiary)'
						: 'var(--tint-jike-blue, #03a9f4)'};
					font-size: 12px;
					font-weight: 500;
					line-height: 18px;
				`}
			>
				{data?.content ?? (
					<input
						placeholder="未选择圈子"
						onChange={(e) => {
							if (isInputCompositingRef.current) {
								return
							}
							handleChangeRef.current(e.target.value)
						}}
						onCompositionStart={() => {
							isInputCompositingRef.current = true
						}}
						onCompositionEnd={(e) => {
							isInputCompositingRef.current = false
							handleChangeRef.current((e.target as HTMLInputElement).value)
						}}
						autoComplete="off"
						css={css`
							border: unset;
							background-color: unset;
							min-width: unset;
							width: 61px;
							outline: none;
							color: inherit;
							font-size: inherit;
							line-height: inherit;

							&::placeholder {
								color: var(--tint-placeholder);
							}
						`}
					/>
				)}
			</div>
			{cleanable && !isPlaceholderDisplay && (
				<ClickWrapper
					onClick={onClean}
					css={css`
						margin: 3px 4px;
						display: flex;
						align-items: center;
						justify-content: center;
					`}
				>
					<CloseIcon
						css={css`
							width: 12px;
							height: 12px;
							color: var(--tint-tertiary);
						`}
					/>
				</ClickWrapper>
			)}
		</Component>
	)
}
